import { Button, IconButton, Tooltip, Typography } from '@material-ui/core'
import classNames from 'classnames'
import { SaveButton } from 'components/Common/Button/SaveButton'
import { MoneyPerQuantityInput } from 'components/Common/Input/MoneyPerQuantityInput'
import { QuantityField } from 'components/Common/UnitEditor/QuantityField'
import ValueLabel from 'components/Common/ValueLabel/ValueLabel'
import { useArticleIdFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/ArticleIdFilter/useArticleIdFilter'
import { useResourceIdFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/useResourceIdFilter'
import ScaledCostPriceTable from 'features/CostPriceDetails/ScaledCostPriceTable'
import { isEmpty } from 'lodash'
import { MoneyString } from 'model/Money'
import { QuantityString } from 'model/Quantity'
import { useTranslation } from 'react-i18next'
import { PriceSummaryDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { Key } from 'utils/keyCodes'
import { ReactComponent as FilterAltOffOutlined } from '_icons/filterAltOffOutlined.svg'
import { ReactComponent as FilterAltOutlined } from '_icons/filterAltOutlined.svg'
import { useProjectPriceSummaryDetailsStyles } from '../ProjectFinancialDetails.styles'
import { HomePriceButtonOutlined } from './HomePriceButtonOutlined'
import { useProjectPriceSummaryDetails } from './useProjectPriceSummaryDetails'

const EditableFields = ({
  changedPriceSummary,
  handleChange,
  handleResetToDefaults,
  isDiscountRatioDisabled,
  isDiscountValueDisabled,
  isSurchargeRatioDisabled,
  isSurchargeValueDisabled,
  validationErrors,
  loading,
}: {
  changedPriceSummary: PriceSummaryDto
  handleChange
  handleResetToDefaults: (
    valueType: 'discount' | 'surcharge',
    priceSummary: PriceSummaryDto
  ) => Promise<void>
  isDiscountRatioDisabled: boolean
  isDiscountValueDisabled: boolean
  isSurchargeRatioDisabled: boolean
  isSurchargeValueDisabled: boolean
  validationErrors: Record<string, string>
  loading: Record<string, boolean>
}) => {
  const { t } = useTranslation()

  if (!changedPriceSummary) {
    return null
  }

  return (
    <>
      <QuantityField
        value={changedPriceSummary.surchargeRatio}
        name="surchargeRatio"
        disabled={isSurchargeRatioDisabled}
        error={Boolean(validationErrors['surchargeRatio'])}
        style={{ gridArea: 'surchp' }}
        label={
          <span>
            {t('common:surcharge')}
            <HomePriceButtonOutlined
              handleResetToDefaults={() =>
                handleResetToDefaults('surcharge', changedPriceSummary)
              }
              isManuallySet={changedPriceSummary.isSurchargeManuallySet}
              isLoading={
                loading[`delete-surcharge-${changedPriceSummary.materialId}`]
              }
            />
          </span>
        }
        onChange={(value) => handleChange('surchargeRatio', value)}
        textAlign="right"
      />

      <QuantityField
        value={changedPriceSummary.surchargeValue}
        name="surchargeValue"
        disabled={isSurchargeValueDisabled}
        error={Boolean(validationErrors['surchargeValue'])}
        style={{ gridArea: 'surchv' }}
        label={
          <span>
            {t('common:surcharge')}
            <HomePriceButtonOutlined
              isManuallySet={changedPriceSummary.isSurchargeManuallySet}
              handleResetToDefaults={() =>
                handleResetToDefaults('surcharge', changedPriceSummary)
              }
              isLoading={
                loading[`delete-surcharge-${changedPriceSummary.materialId}`]
              }
            />

            {/* {changedPriceSummary.isSurchargeManuallySet && (
              <Tooltip title={t('project:reset-to-default')}>



                <HomeWorkOutlined
                  className="reset-button"
                  onClick={() =>
                    handleResetToDefaults('surcharge', changedPriceSummary)
                  }
                />
              </Tooltip>
            )} */}
          </span>
        }
        onChange={(value) => handleChange('surchargeValue', value)}
        textAlign="right"
      />

      <QuantityField
        value={changedPriceSummary.discountRatio}
        name="discountRatio"
        showMinus
        disabled={isDiscountRatioDisabled}
        style={{ gridArea: 'discp' }}
        error={Boolean(validationErrors['discountRatio'])}
        label={
          <span>
            {t('common:discount')}
            <HomePriceButtonOutlined
              isManuallySet={changedPriceSummary.isDiscountManuallySet}
              handleResetToDefaults={() =>
                handleResetToDefaults('discount', changedPriceSummary)
              }
              isLoading={
                loading[`delete-discount-${changedPriceSummary.materialId}`]
              }
            />
            {/*
            {changedPriceSummary.isDiscountManuallySet && (
              <Tooltip title={t('project:reset-to-default')}>
                <HomeWorkOutlined
                  className="reset-button"
                  onClick={() =>
                    handleResetToDefaults('discount', changedPriceSummary)
                  }
                />
              </Tooltip>
            )} */}
          </span>
        }
        onChange={(value) => handleChange('discountRatio', value)}
        textAlign="right"
      />
      <QuantityField
        value={changedPriceSummary.discountValue}
        // allowNegative
        name="discountValue"
        showMinus
        style={{ gridArea: 'discv' }}
        disabled={isDiscountValueDisabled}
        error={Boolean(validationErrors['discountValue'])}
        label={
          <span>
            {t('common:discount')}
            <HomePriceButtonOutlined
              isManuallySet={changedPriceSummary.isDiscountManuallySet}
              handleResetToDefaults={() =>
                handleResetToDefaults('discount', changedPriceSummary)
              }
              isLoading={
                loading[`delete-discount-${changedPriceSummary.materialId}`]
              }
            />
          </span>
        }
        onChange={(value) => handleChange('discountValue', value)}
        textAlign="right"
      />
    </>
  )
}

const ReadOnlyFields = ({
  changedPriceSummary,
}: {
  changedPriceSummary: PriceSummaryDto
}) => {
  const { t } = useTranslation()

  if (!changedPriceSummary) {
    return null
  }

  return (
    <>
      <ValueLabel
        value={QuantityString(changedPriceSummary.surchargeRatio)}
        label={t('common:surcharge')}
        valueLabelRootStyles={{
          whiteSpace: 'pre-line',
          gridArea: 'surchp',
        }}
      />
      <ValueLabel
        value={MoneyString(changedPriceSummary.surchargeValue)}
        label={t('common:surcharge')}
        valueLabelRootStyles={{
          whiteSpace: 'pre-line',
          gridArea: 'surchv',
        }}
      />

      <ValueLabel
        value={QuantityString(changedPriceSummary.discountRatio)}
        label={t('common:discount')}
        valueLabelRootStyles={{
          whiteSpace: 'pre-line',
          gridArea: 'discp',
        }}
      />
      <ValueLabel
        value={MoneyString(changedPriceSummary.discountValue)}
        label={t('common:discount')}
        valueLabelRootStyles={{
          whiteSpace: 'pre-line',
          gridArea: 'discv',
        }}
      />
    </>
  )
}

export const ProjectMaterialPriceSummary = (props: {
  priceSummary: PriceSummaryDto
}) => {
  const {
    handleChange,
    handleSubmit,
    changed,
    handleReset,
    changedPriceSummary,
    saving,
    handleResetToDefaults,
    isDiscountRatioDisabled,
    isDiscountValueDisabled,
    isSurchargeRatioDisabled,
    isSurchargeValueDisabled,
    validationErrors,
    loading,
  } = useProjectPriceSummaryDetails(props.priceSummary)
  const classes = useProjectPriceSummaryDetailsStyles()

  const { t } = useTranslation()

  const { handleChangeFilter, filterIsActive, articleIdFilter } =
    useArticleIdFilter()

  const { resourceIdFilter } = useResourceIdFilter()

  if (props.priceSummary?.isWorkingStep) {
    return null
  }

  if (resourceIdFilter.active) {
    return null
  }

  if (
    articleIdFilter.active &&
    !articleIdFilter.filter.enabledFilters.includes(
      props.priceSummary.materialId
    )
  ) {
    return null
  }

  return (
    <form
      onSubmit={handleSubmit}
      onReset={handleReset}
      onKeyDown={(e) => e.key === Key.Escape && handleReset(e)}
      className={classes.form}
    >
      <div
        id={
          props.priceSummary.materialId ||
          props.priceSummary.workingStep?.resource?.id
        }
        className={classNames(classes.priceSummaryGrid)}
      >
        <div className={classes.priceSummaryDescription}>
          <div>
            <div className={classes.name}>
              {changedPriceSummary.description}
            </div>
            <div>{changedPriceSummary.materialId}</div>
          </div>
        </div>
        <div className={classes.usage}>
          <Typography variant="body2">
            {QuantityString(changedPriceSummary.weight)}
          </Typography>
          <Typography variant="caption">
            {QuantityString(changedPriceSummary.totalQuantity)}
          </Typography>
        </div>

        <div className={classNames(classes.rate, 'input-quantity')}>
          <MoneyPerQuantityInput
            value={changedPriceSummary.rate}
            name="rate"
            rootClassName="input-quantity-field"
            // label={
            //   <span>
            //     {t('common:rate')}
            //     {changedPriceSummary.isCostPriceManuallySet && (
            //       <Tooltip title={t('project:reset-to-default')}>
            //         <HomeWorkOutlined
            //           className="reset-button"
            //           onClick={() =>
            //             handleResetToDefaults('surcharge')
            //           }
            //         />
            //       </Tooltip>
            //     )}
            //   </span>
            // }
            onChange={(e) => handleChange('rate', e.currentTarget.value)}
            // defaultUnits={['kg', 'g']}
            defaultUnits={[props.priceSummary.totalQuantity.unit]}
          />
          {changedPriceSummary.isCostPriceManuallySet && (
            <Tooltip title={t('project:reset-to-default')}>
              <span>
                <HomePriceButtonOutlined
                  isManuallySet={changedPriceSummary.isCostPriceManuallySet}
                  handleResetToDefaults={() =>
                    handleResetToDefaults('costPrice')
                  }
                  isLoading={
                    loading[
                      `delete-cost-price-${props.priceSummary.materialId}`
                    ]
                  }
                />
              </span>
            </Tooltip>
          )}
        </div>
        <div className={classes.filterButton}>
          <Tooltip
            title={t('project:filter-by-article-id', 'filter by article')}
          >
            <IconButton
              onClick={() => handleChangeFilter(props.priceSummary.materialId)}
              style={{ alignSelf: 'center' }}
              size="small"
            >
              {filterIsActive(props.priceSummary.materialId) ? (
                <FilterAltOffOutlined />
              ) : (
                <FilterAltOutlined />
              )}
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.scaledValueTable}>
          <ScaledCostPriceTable
            scaledPrices={changedPriceSummary.staggeredCostPrices}
            hideSelectedColumn
            hideHeader
          />
        </div>
        {/* <div className={classes.priceSummaryValues}> */}
        <ValueLabel
          label={'costs [total]'}
          labelTranslationKey={'project:costs-total'}
          value={MoneyString(changedPriceSummary.totalCostPrice)}
          valueLabelRootStyles={{
            whiteSpace: 'pre-line',
            gridArea: 'cost',
          }}
        />
        {props.priceSummary.canEditDiscountOrSurcharge &&
        !props.priceSummary.isPurchasePartWithoutArticle ? (
          <EditableFields
            changedPriceSummary={changedPriceSummary}
            handleChange={handleChange}
            handleResetToDefaults={handleResetToDefaults}
            isDiscountRatioDisabled={isDiscountRatioDisabled}
            isDiscountValueDisabled={isDiscountValueDisabled}
            isSurchargeRatioDisabled={isSurchargeRatioDisabled}
            isSurchargeValueDisabled={isSurchargeValueDisabled}
            validationErrors={validationErrors}
            loading={loading}
          />
        ) : (
          <ReadOnlyFields changedPriceSummary={changedPriceSummary} />
        )}
        {/* <div
            className={classNames(
              // classes.priceSummaryValue,
              // classes.salesPriceValue
            )}
          > */}
        <ValueLabel
          label={'sales price'}
          labelTranslationKey={'project:sales-price'}
          value={MoneyString(changedPriceSummary.totalSalesPrice)}
          valueLabelRootStyles={{
            gridArea: 'salesPrice',
          }}
        />
        {/* </div> */}
        {/* </div> */}

        {/*
          {props.priceSummary.canEditDiscountOrSurcharge &&
          !props.priceSummary.isPurchasePartWithoutArticle ? (
            <EditableFields
              changedPriceSummary={changedPriceSummary}
              handleChange={handleChange}
              handleResetToDefaults={handleResetToDefaults}
              isDiscountRatioDisabled={isDiscountRatioDisabled}
              isDiscountValueDisabled={isDiscountValueDisabled}
              isSurchargeRatioDisabled={isSurchargeRatioDisabled}
              isSurchargeValueDisabled={isSurchargeValueDisabled}
              validationErrors={validationErrors}
            />
          ) : (
            <ReadOnlyFields changedPriceSummary={changedPriceSummary} />
          )}

          <ValueLabel
            label={'sales price'}
            labelTranslationKey={'project:sales-price'}
            value={MoneyString(changedPriceSummary.totalSalesPrice)}
          />
        </div>
     */}
      </div>
      {changed && (
        <div className={classes.actions}>
          <>
            <ul>
              {Object.keys(validationErrors).map((errKey) => (
                <li key={errKey}>
                  <Typography color="secondary" variant="caption">
                    {validationErrors[errKey]}
                  </Typography>
                </li>
              ))}
            </ul>
            <Button
              size="small"
              type="reset"
              variant="outlined"
              color="secondary"
            >
              cancel
            </Button>
            <SaveButton
              disabled={!isEmpty(validationErrors)}
              size="small"
              saving={saving}
            />
          </>
        </div>
      )}
    </form>
  )
}
